import { DateTime } from 'luxon'
import { donationIsoToUkDate } from '@/helpers/recurring/dates'
import { getCurrentUkTaxYear } from '@/helpers/ukTaxYear'

/** API financial year key, e.g. 2025-2026 → display 2025–26 */
export function formatPeriodShortLabel(periodKey: string): string {
  const parts = periodKey.split('-').map((p) => parseInt(p, 10))
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return periodKey
  const [start, end] = parts
  return `${start}–${String(end).slice(-2)}`
}

/** UK tax year period key containing the given ISO date. */
export function getUkTaxYearPeriodKeyForDate(isoDate: string): string {
  const dt = DateTime.fromISO(isoDate, { zone: 'Europe/London' })
  const taxYearStart = DateTime.fromObject(
    { year: dt.year, month: 4, day: 6 },
    { zone: 'Europe/London' }
  )
  const startYear = dt >= taxYearStart ? dt.year : dt.year - 1
  const endYear = startYear + 1
  return `${startYear}-${endYear}`
}

/** Query param used to open the giving record on a specific tax year. */
export const GIVING_RECORD_YEAR_QUERY = 'year'

const PERIOD_KEY_PATTERN = /^\d{4}-\d{4}$/

/** Reads a `?year=2025-2026` query value, or null if it is missing or malformed. */
export function parseGivingRecordYearQuery(value: unknown): string | null {
  if (typeof value !== 'string' || !PERIOD_KEY_PATTERN.test(value)) return null
  return value
}

/**
 * Tax years shown in the giving-record dropdown. Always includes a selected
 * period even when the API omitted that year because it has no donations yet.
 */
export function collectGivingRecordPeriodKeys(input: {
  apiPeriods: string[]
  rowPeriodKeys: string[]
  selectedPeriod?: string | null
}): string[] {
  const periods = new Set([
    ...input.apiPeriods.filter((period) => parseGivingRecordYearQuery(period)),
    ...input.rowPeriodKeys.filter((period) => parseGivingRecordYearQuery(period)),
  ])
  const selected = parseGivingRecordYearQuery(input.selectedPeriod)
  if (selected) periods.add(selected)
  return [...periods].sort((a, b) => b.localeCompare(a))
}

/**
 * Donors may record a gift in the current tax year or up to this many tax
 * years before it. In 2026/27 the earliest day is 6 April 2020.
 */
export const RECORDABLE_DONATION_TAX_YEAR_LOOKBACK = 6

/**
 * First day a donor may use when adding a one-off or recurring donation.
 * Inclusive: a gift on this day is allowed, the day before is not.
 */
export function earliestRecordableDonationIsoDate(
  reference = DateTime.now().setZone('Europe/London')
): string {
  const earliestStartYear =
    getCurrentUkTaxYear(reference).startYear - RECORDABLE_DONATION_TAX_YEAR_LOOKBACK
  return `${earliestStartYear}-04-06`
}

/** Whether an ISO date is on or after the recordable cutoff. */
export function isRecordableDonationDate(
  isoDate: string,
  reference = DateTime.now().setZone('Europe/London')
): boolean {
  return donationIsoToUkDate(isoDate) >= earliestRecordableDonationIsoDate(reference)
}

/**
 * A new date must fall inside the six-tax-year window. An unchanged date is
 * kept even when it is older, so a historic gift can still be corrected
 * without being moved further into the past or deleted.
 */
export function isDonationDateChangeAllowed(
  nextIsoDate: string,
  currentIsoDate?: string | null,
  reference = DateTime.now().setZone('Europe/London')
): boolean {
  const nextDay = donationIsoToUkDate(nextIsoDate)
  if (currentIsoDate && donationIsoToUkDate(currentIsoDate) === nextDay) {
    return true
  }
  return isRecordableDonationDate(nextDay, reference)
}

/** Inclusive ISO date bounds (YYYY-MM-DD) for a UK tax year period key. */
export function ukTaxYearIsoDateBounds(
  periodKey: string
): { min: string; max: string } | null {
  const parsed = parseGivingRecordYearQuery(periodKey)
  if (!parsed) return null
  const startYear = parseInt(parsed.slice(0, 4), 10)
  return {
    min: `${startYear}-04-06`,
    max: `${startYear + 1}-04-05`,
  }
}

/**
 * Date to pre-fill when recording a gift for a specific tax year. Uses today
 * when that day falls in the year, otherwise the last day of the year.
 */
export function defaultIsoDateForUkTaxYear(
  periodKey: string,
  reference = DateTime.now().setZone('Europe/London')
): string {
  const bounds = ukTaxYearIsoDateBounds(periodKey)
  if (!bounds) return reference.toISODate() ?? ''
  const today = reference.startOf('day')
  const start = DateTime.fromISO(bounds.min, { zone: 'Europe/London' })
  const end = DateTime.fromISO(bounds.max, { zone: 'Europe/London' })
  if (today >= start && today <= end) return today.toISODate() ?? bounds.max
  if (today < start) return bounds.min
  return bounds.max
}

/** Router location that opens the giving record filtered to a tax year. */
export function donationsRouteForPeriod(periodKey: string) {
  return {
    name: 'Donations' as const,
    query: { [GIVING_RECORD_YEAR_QUERY]: periodKey },
  }
}

export {
  isManualDonationSource,
  donationSourceCategory,
  donationSourceLabel,
  donationSourcePillClass,
} from '@/helpers/donationSource'
export type { DonationSourceCategory } from '@/types/donationSource'
